import React from 'react';
import ReactDom from 'react-dom';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import Button from '@fuww/library/src/Button';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { useSiteContext } from '../components/SiteContext';
import LinkAndAnchor from '../components/LinkAndAnchor';
import applyButtonBlacklist from './applyButtonBlacklist';
import {
  TransformedJobProperties,
} from '../components/Jobs/JobDetail/JobProperties';

const FormattedMessage: React.FC<{ message: string }> = ({ message }) => {
  const intl = useIntl();
  return <>{intl.formatMessage({ id: message })}</>;
};

export interface TemplateProperties {
  job: TransformedJobProperties;
  locale: string;
  applyUrl: string | null;
  profiles: Record<string, unknown>;
}

export type TemplatesProperty = React.ComponentType<TemplateProperties>
| React.ElementType<TemplateProperties>
| Record<string, unknown> | undefined;

export interface TemplatesProperties {
  CompanyDescription?: React.ElementType<TemplateProperties>;
  Description?: React.ElementType<TemplateProperties>;
  properties?: Record<string, unknown>;
  [key: keyof TemplateDependencies]: TemplatesProperty;
}

const templates: Record<string, TemplatesProperties | undefined> = {};

const getTemplateKey = (
  sourceName: string,
  revision: string,
): string => `${
  sourceName}-${revision}`;

const templateButtonPropertyTypes = {
  href: PropTypes.string.isRequired,
  title: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

type TemplateButtonProperties = InferProps<
  typeof templateButtonPropertyTypes
> & {
  title?: string;
  className: string;
};

const TemplateButton = ({
  href,
  title,
  children,
  className,
}: TemplateButtonProperties) => {
  const { locale } = useSiteContext();
  const { query: { slug, id } } = useRouter();
  const target = href === 'job-apply' ? undefined : '_blank';
  const relationship = target ? 'noopener noreferrer' : undefined;
  const classNames = className.split(/\s+/);
  const anchorClassName = classNames
    .filter((name: string) => (name || '').startsWith('fu-')).join(' ');
  const buttonClassName = classNames
    .filter((name: string) => !(name || '').startsWith('fu-')).join(' ');

  return (
    <LinkAndAnchor
      href={href === 'job-apply' ? undefined : href}
      className={anchorClassName}
      target={target}
      rel={relationship}
      title={title}
      route={`job-apply.${locale}`}
      params={{ slug, id }}
      useRouter={href === 'job-apply'}
    >
      <Button className={buttonClassName}>{children}</Button>
    </LinkAndAnchor>
  );
};

TemplateButton.propTypes = templateButtonPropertyTypes;

const TemplateI18n = (
  { message } : { message: string },
) => (<FormattedMessage message={message} />);

TemplateI18n.propTypes = {
  message: PropTypes.string,
};

TemplateI18n.defaultProps = {
  message: '',
};

const EmptyButton = () => null;

interface TemplateDependencies {
  react: typeof React;
  'react-dom': typeof ReactDom;
  'prop-types': typeof PropTypes;
  'meteor/fuww:react-tapi18n': { I18n: typeof TemplateI18n };
  'react-material-web-components/components/Button': typeof EmptyButton
  | typeof TemplateButton;
  '@fuww/library/components/Button': typeof EmptyButton | typeof TemplateButton;
  '@rmwc/button': { Button: typeof EmptyButton | typeof TemplateButton };
  'styled-components': typeof styled;
  [key: string]: unknown;
}

export const getTemplateDependencies = (
  hasButton: boolean,
): TemplateDependencies => ({
  react: React,
  'react-dom': ReactDom,
  'prop-types': PropTypes,
  'meteor/fuww:react-tapi18n': {
    I18n: TemplateI18n,
  },
  'react-material-web-components/components/Button':
    hasButton ? TemplateButton : EmptyButton,
  '@fuww/library/components/Button': hasButton
    ? TemplateButton : EmptyButton,
  '@rmwc/button': { Button: hasButton ? TemplateButton : EmptyButton },
  'styled-components': styled,
});

const fetchTemplate = async (
  sourceName: string,
  revision: string,
  origin: string,
): Promise<TemplatesProperties> => {
  const require = (path :string) => getTemplateDependencies(
    applyButtonBlacklist.includes(sourceName),
  )[path];

  const result = await fetch(
    `${origin}/job-templates/${encodeURI(sourceName)}.js?v=${revision}`,
  );

  // eslint-disable-next-line @typescript-eslint/no-implied-eval
  return new Function(
    'require', `return ${await result.text()}`,
  )(require);
};

const importJobTemplate = async (
  sourceName: string,
  revision: string,
  origin: string,
) => {
  const templateKey = getTemplateKey(sourceName, revision);
  const cachedTemplate: TemplatesProperties
  | undefined = templates[templateKey];

  if (cachedTemplate) {
    return cachedTemplate;
  }

  const fetchedTemplate = fetchTemplate(sourceName, revision, origin);

  templates[templateKey] = fetchedTemplate as unknown as TemplatesProperties;

  return fetchedTemplate;
};

export default importJobTemplate;
